/**
 * 打车账号绑定 —— `/api/ride-credential`（M2.5，2026-09-18 后端已就绪并联调通过）。
 *
 * ★ **这是"一人一个账号"那一步。** 在它之前全系统共用一个开发者的微信出行账号 ——
 *   费用和行程隐私都压在那一个人头上。现在每个用户绑自己的那段 Token 才能叫车。
 *
 * ★★ 三条贯穿整个模块的纪律，做界面前必须先认下：
 *
 *  1. **出参一个明文字段都没有**，只有 {@link rideCredentialStatusSchema} 的 `masked`。
 *     ⚠⚠ 如果你在某个响应里看到了完整 Token，**那是 bug，请告诉后端** —— 别自己"兼容"它。
 *  2. **这段 Token 拿着能下真单、能看该账号的全部行程**。所以：
 *     输入框不回显全文、提交后立即清空、**不写 localStorage / sessionStorage**、
 *     **不打进前端日志 / 埋点 / 错误上报**。
 *     ⚠ `masked` 可以显示在卡片和设置页上，但**不要读出来 / 不要进语音播报**。
 *  3. **绑定成功不等于 Token 可用**（后端是"懒发现"）：录入时只校验**格式**，
 *     真伪要到第一次实际叫车时才由供应商判定。
 *     ⇒ 成功文案**不能**说「已验证」「可以用了」；而 `INVALID` 的文案也**不能只说"过期了"**，
 *       用户完全可能**刚绑完三秒**就看到它。见 `cards.ts` 的 `RIDE_TOKEN_REASONS`。
 */
import { z } from 'zod'

/**
 * 绑定状态。
 *
 * ⚠ `INVALID` = **绑过，但那个 Token 用不了了**（过期，或复制时少了一截）——
 *   与 `UNBOUND`（从没绑过）是两种完全不同的用户处境，文案不许共用一句话。
 */
export const RIDE_CREDENTIAL_STATUSES = ['BOUND', 'UNBOUND', 'INVALID'] as const
export type RideCredentialStatus = (typeof RIDE_CREDENTIAL_STATUSES)[number]

/**
 * `GET` / `POST` / `DELETE` 三个端点回的是**同一个形状**。
 *
 * ⚠ `status` 声明成 `string` 而不是 `z.enum`：与卡片里的 `reason` 同一个理由 ——
 *   后端将来多一个状态（比如 `EXPIRED`）时，enum 会让整个响应 parse 失败，
 *   于是"查一下我绑没绑"这件事在界面上直接变成一个错误。收下来、认不出的按未绑处理更稳。
 *   判定用 {@link isBoundStatus}。
 */
export const rideCredentialStatusSchema = z.looseObject({
  /** `BOUND` / `UNBOUND` / `INVALID`。见 {@link RIDE_CREDENTIAL_STATUSES}。 */
  status: z.string().min(1),
  /** 形如 `ff1k****jkI`。⚠ **仅展示**，不可还原，也绝不能回传。未绑时为 null。 */
  masked: z.string().nullable().default(null),
  /** 形如 `2026-09-18T10:00:00`。⚠ 不带时区，与 D-1/D-2 的时间串同一读法。 */
  boundAt: z.string().nullable().default(null),
})
export type RideCredential = z.infer<typeof rideCredentialStatusSchema>

/** 绑上了没有。⚠ 认不出的状态一律**按没绑**算 —— 比按绑了算安全。 */
export function isBoundStatus(status: string | null | undefined): boolean {
  return status === 'BOUND'
}

/**
 * 绑定请求体。
 *
 * ⚠⚠ **strictObject，全包第二处**（第一处是 `payments.ts` 的 `createPaymentRequestSchema`）。
 *   理由同源：**请求体里不许放 `userId`** —— 后端只认令牌里的身份，放了也会被忽略，
 *   但"被忽略"是**今天**的行为。让它在类型检查与单测里当场红掉，比指望后端永远忽略要牢靠。
 *
 * ⚠ `token` 是用户**粘贴的原文**，原样发过去：不要 trim（后端要靠不可见字符判格式错）、
 *   不要截断、不要大小写转换。
 */
export const bindRideCredentialRequestSchema = z.strictObject({
  token: z.string().min(1),
})
export type BindRideCredentialRequest = z.infer<typeof bindRideCredentialRequestSchema>
